import { ApolloClient, ApolloProvider, InMemoryCache, createHttpLink } from '@apollo/client';
import { setContext } from '@apollo/client/link/context';
import { useContext, useMemo } from 'react';
import { CognitoContext } from '../CognitoProvider';

const { REACT_APP_API_URL: API_URL } = process.env;

interface Props {
  children?: React.ReactNode;
}

// Extract signed patient token from URL path for anonymous Dans Fund pages
// Token format: base64(payload).base64(signature)
const getPatientTokenFromUrl = (): string | null => {
  const path = window.location.pathname;
  // Match patterns like /dans-fund-questionnaire/:token or /dans-fund/opt-out/:token
  // Token contains base64url characters and a dot separator
  const questionnaireMatch = path.match(/\/dans-fund-questionnaire\/([A-Za-z0-9_-]+\.[A-Za-z0-9_-]+)/);
  const optOutMatch = path.match(/\/dans-fund\/opt-out\/([A-Za-z0-9_-]+\.[A-Za-z0-9_-]+)/);
  return questionnaireMatch?.[1] || optOutMatch?.[1] || null;
};

const ApolloWrapper = ({ children }: Props) => {
  const cognitoContext = useContext(CognitoContext);
  if (cognitoContext === null) {
    throw new Error('No AuthContext');
  }

  const { token } = cognitoContext;

  const apolloClient = useMemo(() => {
    const httpLink = createHttpLink({
      uri: API_URL,
    });

    const authLink = setContext((_, { headers }) => {
      const patientToken = getPatientTokenFromUrl();

      return {
        headers: {
          ...headers,
          authorization: token ? `Bearer ${token}` : '',
          // Include signed patient token for anonymous users on Dans Fund pages
          // Backend will verify signature and extract patient ID
          ...(patientToken && !token ? { 'x-patient-token': patientToken } : {}),
        },
      };
    });

    return new ApolloClient({
      link: authLink.concat(httpLink),
      cache: new InMemoryCache(),
    });
  }, [token]);

  return <ApolloProvider client={apolloClient}>{children}</ApolloProvider>;
};

export default ApolloWrapper;
